// src/components/BookingAssistBubble.jsx
import React, { useState, useEffect, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";

const BookingAssistBubble = () => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [bookingState, setBookingState] = useState(null);
  const [timeLeft, setTimeLeft] = useState(0);
  const [isVisible, setIsVisible] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [position, setPosition] = useState({ x: window.innerWidth - 80, y: window.innerHeight - 150 });
  const dragStart = useRef({ x: 0, y: 0 });

  // Check for pending booking
  useEffect(() => {
    const checkBooking = () => {
      try {
        const stored = localStorage.getItem("pending_employee_booking");
        if (!stored) {
          setBookingState(null);
          setIsVisible(false);
          return;
        }

        const parsed = JSON.parse(stored);
        const now = Date.now();
        const expiry = parsed.expiryTime;
        const remaining = Math.max(0, Math.floor((expiry - now) / 1000));

        if (remaining > 0) {
          setBookingState(parsed);
          setTimeLeft(remaining);
          // Don't show if we are already on the booking page
          setIsVisible(location.pathname !== "/employee/book");
        } else {
          // Expired
          localStorage.removeItem("pending_employee_booking");
          setBookingState(null);
          setIsVisible(false);
        }
      } catch (e) {
        console.error("Error parsing pending booking:", e);
      }
    };

    // Check immediately
    checkBooking();

    // Check every second
    const interval = setInterval(checkBooking, 1000);
    return () => clearInterval(interval);
  }, [location.pathname]);

  // Handle Dragging
  const handleDragStart = (e) => {
    e.preventDefault();
    const clientX = e.type === "touchstart" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchstart" ? e.touches[0].clientY : e.clientY;
    
    setIsDragging(true);
    dragStart.current = {
      x: clientX - position.x,
      y: clientY - position.y
    };
  };

  const handleDragMove = (e) => {
    if (!isDragging) return;
    
    const clientX = e.type === "touchmove" ? e.touches[0].clientX : e.clientX;
    const clientY = e.type === "touchmove" ? e.touches[0].clientY : e.clientY;

    const newX = clientX - dragStart.current.x;
    const newY = clientY - dragStart.current.y;

    setPosition({
      x: Math.min(Math.max(0, newX), window.innerWidth - 60),
      y: Math.min(Math.max(0, newY), window.innerHeight - 60)
    });
  };

  const handleDragEnd = () => {
    setIsDragging(false);
  };

  useEffect(() => {
    if (isDragging) {
      window.addEventListener("mousemove", handleDragMove);
      window.addEventListener("mouseup", handleDragEnd);
      window.addEventListener("touchmove", handleDragMove, { passive: false });
      window.addEventListener("touchend", handleDragEnd);
    }
    return () => {
      window.removeEventListener("mousemove", handleDragMove);
      window.removeEventListener("mouseup", handleDragEnd);
      window.removeEventListener("touchmove", handleDragMove);
      window.removeEventListener("touchend", handleDragEnd);
    };
  }, [isDragging]);

  const handleClick = () => {
    if (!isDragging && bookingState) {
      navigate("/employee/book");
    }
  };

  if (!isVisible || !bookingState) return null;

  return (
    <div
      className="fixed z-[9999] cursor-pointer touch-none select-none transition-transform hover:scale-105 active:scale-95"
      style={{ 
        left: position.x, 
        top: position.y,
        transition: isDragging ? 'none' : 'all 0.3s ease'
      }}
      onMouseDown={handleDragStart}
      onTouchStart={handleDragStart}
      onClick={handleClick}
    >
      <div className="relative group">
        {/* Pulse Effect */}
        <div className="absolute inset-0 bg-red-500 rounded-full animate-ping opacity-20"></div>
        
        {/* Main Bubble */}
        <div className="relative w-16 h-16 bg-gradient-to-br from-red-500 to-pink-600 rounded-full shadow-lg border-2 border-white flex flex-col items-center justify-center text-white overflow-hidden">
          <span className="text-xs font-bold uppercase tracking-tighter">Finish</span>
          <span className="text-xl font-black font-mono leading-none">{timeLeft}s</span>
        </div>

        {/* Tooltip */}
        <div className="absolute right-full top-1/2 -translate-y-1/2 mr-3 bg-gray-900 text-white text-xs px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none">
          Complete Booking
          <div className="absolute top-1/2 -translate-y-1/2 -right-1 w-2 h-2 bg-gray-900 rotate-45"></div>
        </div>
      </div>
    </div>
  );
};

export default BookingAssistBubble;
